import { formatDateTime, formatMoney, t } from "@hakili/i18n";
import { GuardedButton } from "@hakili/ui";
import { AlertTriangle, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * `round CN-A`, `CN-A.2`: **« Arrivées », to the drawing of 10 September**
 * (`hakili-console.jsx`, `ArriveesScreen`), `WF-CON-21` to `23`.
 *
 * **A flow, never a queue of pending decisions.** Every file born in the last
 * thirty days, newest first, with the assignment it was born with , `P1-DOS-010`
 * decided it in the birth's own transaction , and the one standing today. The
 * screen has no confirmation control anywhere: what it offers is a CORRECTION,
 * through the same door as every other reassignment.
 *
 * ## What the drawing shows that this screen does not, and why
 *
 * - The excluded candidates, person by person with a reason. `05` §2.4.7 puts
 *   them in `intelligence.assignment_run_candidates`, phase 3's table
 *   (`08-01`), and the birth returns them but persists them nowhere. The card
 *   carries the round's own sentence instead of a list nobody stored.
 * - Each candidate's weighted load beside the selector. No door answers a
 *   person's load today (`WF-CON-25`, `CN-B`); a dash is not a number, and the
 *   drawing's figure is omitted rather than invented (`R81`).
 * - « Jouer une arrivée », the demonstration device. `WF-CON-24`: it must not
 *   exist outside a demo flag; here it does not exist. An arrival is the
 *   client's gesture (`WF-CLI-08`), and the drawing side is driven by it.
 */

export interface ArrivalView {
  readonly id: string;
  readonly reference: string;
  readonly clientName: string;
  readonly vehicleLabel: string;
  readonly sellerName: string;
  readonly intention: string;
  readonly amountFinanced: number;
  readonly createdAt: string;
  readonly ownerUserId: string | null;
  readonly ownerName: string | null;
  readonly birthReason: string;
  readonly corrected: boolean;
  readonly correctedBy: string | null;
  readonly latestReason: string;
  /**
   * `U-3b`, `M1` (`P3-CNW-001`, WF-CON-21 step 3): **the run that decided**, read from the audit
   * (`intelligence.assignment_runs`, 05 §2.12.1) and never recomputed here , the rule it fired and
   * every candidate with its load, the excluded with their reasons. `null` on a file born before
   * the audit existed, and the screen keeps its sentence rather than inventing one (R81).
   */
  readonly run: {
    readonly ruleFired: string;
    readonly reason: string;
    readonly chosenUserId: string | null;
    readonly candidates: readonly {
      readonly userId: string;
      readonly fullName: string;
      readonly eligible: boolean;
      readonly exclusionReason: string | null;
      readonly weightedLoad: number | null;
      readonly chosen: boolean;
    }[];
  } | null;
  /** WF-CON-22: nobody was a candidate , the file exists unassigned and stands in the rose band (I17). */
  readonly exception: boolean;
}

export interface AssignableView {
  readonly id: string;
  readonly fullName: string;
  readonly disponibilite: string | null;
  readonly retourLe: string | null;
}

export interface ArriveesProps {
  readonly arrivals: readonly ArrivalView[];
  readonly assignable: readonly AssignableView[];
  readonly busy: boolean;
  readonly refusal: string | null;
  readonly onReassign: (dossierId: string, userId: string, reason: string) => void;
  readonly onGoTeam: () => void;
}

const DASH = t("common.valeur.tiret");

export function Arrivees(props: ArriveesProps): React.JSX.Element {
  const orphans = props.arrivals.filter((one) => one.ownerUserId === null);
  return (
    <main className="flex-1 overflow-y-auto">
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">{t("console.arrivees.titre")}</h1>
          <p className="mt-1.5 text-sm text-gray-500">{t("console.arrivees.sousTitre")}</p>
        </div>

        {/* The rule, said plainly at the top: what follows is a report. */}
        <div
          data-testid="arrivees-regle"
          className="mb-6 flex items-start gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-3.5"
        >
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
          <p className="text-sm leading-relaxed text-indigo-900">{t("console.arrivees.regle")}</p>
        </div>

        {/* `WF-CON-22`: the exception ABOVE the flow , the one place on this
            screen where something is actually expected of the responsable. */}
        {orphans.length > 0 ? (
          <div
            data-testid="arrivees-exception"
            className="mb-6 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-3.5"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" />
            <p className="text-sm leading-relaxed text-rose-700">
              {orphans.length === 1
                ? t("console.arrivees.exceptionUne")
                : `${String(orphans.length)} ${t("console.arrivees.exceptionPlusieurs")}`}
            </p>
          </div>
        ) : null}

        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">{t("console.arrivees.dernieres")}</h2>
          <span className="text-xs text-gray-500">
            {props.arrivals.length === 0
              ? t("console.arrivees.compteAucune")
              : props.arrivals.length === 1
                ? t("console.arrivees.compteUne")
                : `${String(props.arrivals.length)} ${t("console.arrivees.comptePlusieurs")}`}
          </span>
        </div>

        {props.refusal === null ? null : (
          <p role="alert" data-testid="arrivees-refus" className="mb-3 text-sm text-rose-600">
            {props.refusal}
          </p>
        )}

        <ul className="space-y-3">
          {props.arrivals.map((arrival) => (
            <ArrivalCard key={arrival.id} arrival={arrival} {...props} />
          ))}
        </ul>

        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
          <div>
            <p className="text-sm leading-relaxed text-gray-600">{t("console.arrivees.algorithme")}</p>
            <p className="mt-2 text-sm leading-relaxed text-gray-500">
              {t("console.arrivees.algorithmeSuite")}
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}

function ArrivalCard({
  arrival,
  assignable,
  busy,
  onReassign,
  onGoTeam,
}: ArriveesProps & { readonly arrival: ArrivalView }): React.JSX.Element {
  const orphan = arrival.ownerUserId === null;
  // `WF-CON-22` step 3: for an orphan, EVERY non-deactivated account, the
  // absent included; `WF-CON-23`: for a carried file, the active ones.
  const choices = orphan ? assignable : assignable.filter((one) => one.disponibilite === "actif");
  const [choice, setChoice] = useState<string>(arrival.ownerUserId ?? choices[0]?.id ?? "");
  const target = assignable.find((one) => one.id === choice);
  const unchanged = !orphan && choice === arrival.ownerUserId;
  const targetAway = target?.disponibilite === "absent";

  return (
    <li
      data-testid={`arrivee-${arrival.reference}`}
      className={
        "overflow-hidden rounded-2xl border bg-white " + (orphan ? "border-rose-300" : "border-gray-200")
      }
    >
      <div className="flex items-start gap-4 border-b border-gray-100 px-5 py-3.5">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900">{arrival.clientName}</p>
          <p className="text-xs text-gray-500">
            {arrival.reference} · {arrival.vehicleLabel} · {arrival.sellerName}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-sm font-medium tabular-nums">{formatMoney(arrival.amountFinanced)}</p>
          <p className="text-xs text-gray-500">
            {arrival.intention === "comptant"
              ? t("console.arrivees.achatComptant")
              : t("console.arrivees.financement")}{" "}
            · {formatDateTime(new Date(arrival.createdAt))}
          </p>
        </div>
      </div>

      <div className={"px-5 py-3.5 " + (orphan ? "bg-rose-50" : "")}>
        <div className="flex items-start gap-2.5">
          {orphan ? (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" />
          ) : (
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
          )}
          <div className="flex-1">
            {orphan ? (
              <p className="text-sm font-medium text-rose-700">{t("console.arrivees.orpheline")}</p>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm text-gray-700">
                  {t("console.arrivees.affecteeA")}{" "}
                  <span className="font-medium">{arrival.ownerName ?? DASH}</span>
                </p>
                <span
                  data-testid="arrivee-chip"
                  className={
                    "rounded-full px-2 py-0.5 text-xs font-medium " +
                    (arrival.corrected ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700")
                  }
                >
                  {arrival.corrected
                    ? `${t("console.arrivees.chipCorrigee")} ${arrival.correctedBy ?? DASH}`
                    : t("console.arrivees.chipAutomatique")}
                </span>
              </div>
            )}
            <p className={"mt-0.5 text-xs leading-relaxed " + (orphan ? "text-rose-700" : "text-gray-500")}>
              {arrival.corrected ? arrival.latestReason : arrival.birthReason}
            </p>
            {/* `U-3b`, `M1`: the excluded, person by person, from the RUN that decided , WF-CON-21
                step 1's own sentence (« Non retenus : <nom>, <raison> »); the file born before the
                audit keeps the round's sentence instead (R81). */}
            {arrival.run === null ? (
              <p className="mt-1 text-sm leading-relaxed text-gray-500">
                {t("console.arrivees.nonRetenusMotif")}
              </p>
            ) : arrival.run.candidates.some((one) => !one.eligible) ? (
              <p className="mt-1 text-sm leading-relaxed text-gray-500" data-testid="arrivee-non-retenus">
                {t("console.arrivees.nonRetenus").replace(
                  "{gens}",
                  arrival.run.candidates
                    .filter((one) => !one.eligible)
                    .map((one) => `${one.fullName}, ${one.exclusionReason ?? ""}`)
                    .join(" ; "),
                )}
              </p>
            ) : (
              <p className="mt-1 text-xs leading-relaxed text-gray-500" data-testid="arrivee-non-retenus">
                {t("console.arrivees.nonRetenusAucun")}
              </p>
            )}
            {orphan || arrival.ownerName === null ? null : (
              <p className="mt-1 text-sm leading-relaxed text-gray-500">
                {t("console.arrivees.dejaDansLaListe").replace("{charge}", arrival.ownerName)}
              </p>
            )}
          </div>
        </div>

        <div className="mt-3 border-t border-gray-100 pt-3">
          <p className={"mb-2 text-xs font-medium " + (orphan ? "text-rose-700" : "text-gray-600")}>
            {orphan ? t("console.arrivees.affecterALaMain") : t("console.arrivees.corriger")}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label={orphan ? t("console.arrivees.affecterALaMain") : t("console.arrivees.corriger")}
              value={choice}
              onChange={(event) => {
                setChoice(event.target.value);
              }}
              className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-indigo-400"
            >
              {choices.map((one) => (
                <option key={one.id} value={one.id}>
                  {one.fullName}
                  {one.disponibilite === "absent" ? ` ${t("console.arrivees.optionAbsent")}` : ""}
                </option>
              ))}
            </select>
            {unchanged && arrival.ownerName !== null ? (
              <GuardedButton
                disabled
                reason={t("console.arrivees.rienACorriger").replace("{charge}", arrival.ownerName)}
                variant="primary"
              >
                {t("console.arrivees.reaffecter")}
              </GuardedButton>
            ) : (
              <button
                type="button"
                data-testid="arrivee-affecter"
                disabled={busy || choice === ""}
                onClick={() => {
                  onReassign(
                    arrival.id,
                    choice,
                    targetAway
                      ? `${t("console.arrivees.motifCorrection")}, ${t("console.arrivees.motifMalgreAbsence")}`
                      : t("console.arrivees.motifCorrection"),
                  );
                }}
                className={
                  "rounded-lg px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40 " +
                  (orphan ? "bg-rose-600 hover:bg-rose-700" : "bg-indigo-600 hover:bg-indigo-700")
                }
              >
                {orphan ? t("console.arrivees.affecterCeDossier") : t("console.arrivees.reaffecter")}
              </button>
            )}
            {targetAway ? (
              <span data-testid="arrivee-absent" className="text-xs font-medium text-amber-700">
                {t("console.arrivees.absentJusquAu")
                  .replace("{charge}", target.fullName)
                  .replace("{date}", target.retourLe ?? DASH)}
              </span>
            ) : null}
          </div>
          {orphan ? (
            <button
              type="button"
              onClick={onGoTeam}
              className="mt-2 text-xs font-medium text-rose-700 hover:text-rose-700"
            >
              {t("console.arrivees.rendreDisponible")}
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------- the wrapper

export interface ArriveesDoors {
  readonly readArrivals: () => Promise<readonly ArrivalView[]>;
  readonly readAssignableWithAvailability: () => Promise<readonly AssignableView[]>;
  readonly reassignDossier: (id: string, userId: string, reason: string) => Promise<void>;
}

export interface ArriveesScreenProps extends ArriveesDoors {
  readonly onGoTeam: () => void;
}

const failureWords = (failure: unknown): string =>
  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee");

export function ArriveesScreen(props: ArriveesScreenProps): React.JSX.Element | null {
  const [state, setState] = useState<{
    readonly arrivals: readonly ArrivalView[];
    readonly assignable: readonly AssignableView[];
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const { readArrivals, readAssignableWithAvailability } = props;

  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        const [arrivals, assignable] = await Promise.all([readArrivals(), readAssignableWithAvailability()]);
        if (run.live) setState({ arrivals, assignable });
      } catch (failure) {
        if (run.live) setRefusal(failureWords(failure));
      }
    })();
    return () => {
      run.live = false;
    };
  }, [readArrivals, readAssignableWithAvailability, epoch]);

  if (state === null) {
    return refusal === null ? null : (
      <p role="alert" data-testid="arrivees-refus" className="text-sm text-rose-600">
        {refusal}
      </p>
    );
  }
  return (
    <Arrivees
      arrivals={state.arrivals}
      assignable={state.assignable}
      busy={busy}
      refusal={refusal}
      onGoTeam={props.onGoTeam}
      onReassign={(id, userId, reason) => {
        void (async () => {
          setBusy(true);
          setRefusal(null);
          try {
            await props.reassignDossier(id, userId, reason);
            setEpoch((now) => now + 1);
          } catch (failure) {
            setRefusal(failureWords(failure));
          } finally {
            setBusy(false);
          }
        })();
      }}
    />
  );
}
